import React, { useState, useEffect } from 'react';
import { 
  FaUserTie, 
  FaStar, 
  FaMapMarkerAlt, 
  FaFilter, 
  FaTimes, 
  FaSort, 
  FaSortAmountDown,
  FaSortAmountUp,
  FaGraduationCap
} from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import MentorProfile from './MentorProfile';

/**
 * Component for displaying mentor search results with filtering options
 */
const MentorResults = ({ searchTerm, useLocation, userLocation }) => {
  const { showError } = useToast();
  
  // Mentors state
  const [mentors, setMentors] = useState([]);
  const [filteredMentors, setFilteredMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // Filtering and sorting state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    specialties: [],
    experience: 0,
    rating: 0,
    availability: []
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Fetch mentors on component mount
  useEffect(() => {
    fetchMentors();
  }, []);
  
  // Apply search, filters, and sorting when they change
  useEffect(() => {
    const filtered = filterMentors();
    sortMentors(filtered);
  }, [mentors, searchTerm, filters, sortBy, sortOrder, useLocation, userLocation]);
  
  // Fetch mentors from API
  const fetchMentors = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/mentors/public');
      const data = response?.data?.data || response?.data?.mentors || response?.data || [];
      const list = Array.isArray(data) ? data : [];
      setMentors(list);
      setFilteredMentors(list);
    } catch (error) {
      console.error('Error fetching mentors:', error);
      showError('Failed to load mentors');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter mentors based on search term and filters
  const filterMentors = () => {
    let result = [...mentors];
    
    // Apply search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        mentor => 
          `${mentor.firstName} ${mentor.lastName}`.toLowerCase().includes(search) ||
          (mentor.expertise && mentor.expertise.toLowerCase().includes(search)) ||
          (mentor.bio && mentor.bio.toLowerCase().includes(search)) ||
          (mentor.specialties && mentor.specialties.some(specialty => 
            specialty.toLowerCase().includes(search)
          )) ||
          (mentor.location?.county && mentor.location.county.toLowerCase().includes(search)) ||
          (mentor.location?.town && mentor.location.town.toLowerCase().includes(search))
      );
    }
    
    // Apply specialties filter
    if (filters.specialties.length > 0) {
      result = result.filter(mentor => {
        if (!mentor.specialties || !Array.isArray(mentor.specialties)) return false;
        return mentor.specialties.some(specialty => 
          filters.specialties.some(filter => 
            specialty.toLowerCase().includes(filter.toLowerCase())
          )
        );
      });
    }
    
    // Apply experience filter
    if (filters.experience > 0) {
      result = result.filter(mentor => mentor.experience >= filters.experience);
    }
    
    // Apply rating filter
    if (filters.rating > 0) {
      result = result.filter(mentor => mentor.rating >= filters.rating);
    }
    
    // Apply availability filter
    if (filters.availability.length > 0) {
      result = result.filter(mentor => {
        if (!mentor.availability || !Array.isArray(mentor.availability)) return false;
        return filters.availability.some(avail => mentor.availability.includes(avail));
      });
    }
    
    // Apply location-based sorting if using location
    if (useLocation && userLocation) {
      // Calculate distance for each mentor that has coordinates
      result = result.map(mentor => {
        if (mentor.location?.coordinates) {
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            mentor.location.coordinates.lat,
            mentor.location.coordinates.lng
          );
          return { ...mentor, distance };
        }
        return { ...mentor, distance: Infinity };
      });
    }
    
    return result;
  };
  
  // Sort filtered mentors
  const sortMentors = (mentors) => {
    let sorted = [...mentors];
    
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`;
          const nameB = `${b.firstName} ${b.lastName}`;
          const comparison = nameA.localeCompare(nameB);
          return sortOrder === 'asc' ? comparison : -comparison;
        });
        break;
      case 'rating':
        sorted.sort((a, b) => {
          const comparison = (b.rating || 0) - (a.rating || 0);
          return sortOrder === 'asc' ? -comparison : comparison;
        });
        break;
      case 'experience':
        sorted.sort((a, b) => {
          const comparison = (b.experience || 0) - (a.experience || 0);
          return sortOrder === 'asc' ? -comparison : comparison;
        });
        break;
      case 'distance':
        if (useLocation && userLocation) {
          sorted.sort((a, b) => {
            const comparison = (a.distance || Infinity) - (b.distance || Infinity);
            return sortOrder === 'asc' ? comparison : -comparison;
          });
        }
        break;
      // Relevance is default and already handled by the API or filterMentors
      default:
        if (useLocation && userLocation) {
          // If using location, sort by distance for relevance
          sorted.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
        }
        break;
    }
    
    setFilteredMentors(sorted);
  };
  
  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const distance = R * c; // Distance in km
    return distance;
  };
  
  const deg2rad = (deg) => {
    return deg * (Math.PI/180);
  };
  
  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters(prevFilters => {
      if (filterType === 'specialties' || filterType === 'availability') {
        // For array filters, toggle the value
        const currentValues = [...prevFilters[filterType]];
        const index = currentValues.indexOf(value);
        
        if (index === -1) {
          currentValues.push(value);
        } else {
          currentValues.splice(index, 1);
        }
        
        return { ...prevFilters, [filterType]: currentValues };
      } else {
        // For other filters, just set the value
        return { ...prevFilters, [filterType]: value };
      }
    });
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      specialties: [],
      experience: 0,
      rating: 0,
      availability: []
    });
    
    // Reset sort to default
    setSortBy('relevance');
    setSortOrder('desc');
  };
  
  // Handle sort change
  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field with default desc order
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  
  // View mentor profile
  const viewMentorProfile = (mentor) => {
    setSelectedMentor(mentor);
    setShowProfileModal(true);
  };
  
  // Close mentor profile modal
  const closeProfileModal = () => {
    setShowProfileModal(false);
  };
  
  // Render star ratings
  const renderRatingStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar key={i} className={i <= rating ? "text-yellow-500" : "text-gray-300"} />
      );
    }
    return <div className="flex space-x-1">{stars}</div>;
  };
  
  // Format distance
  const formatDistance = (distance) => {
    if (!distance || distance === Infinity) return null;
    
    return distance < 1 
      ? `${Math.round(distance * 1000)} m`
      : `${distance.toFixed(1)} km`;
  };
  
  // Common specialty options for filtering
  const specialtyOptions = [
    'Mathematics', 'Chemistry', 'Physics', 'Biology', 'English', 
    'Computer Science', 'History', 'Geography', 'Languages', 'Art'
  ];
  
  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h2 className="text-2xl font-bold flex items-center mb-4 md:mb-0">
            <FaUserTie className="mr-2 text-primary" /> Mentors & Experts
            {!loading && <span className="ml-2 text-base font-normal text-gray-600">({filteredMentors.length} results)</span>}
          </h2>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
            >
              <FaFilter className="mr-2" /> 
              {showFilters ? 'Hide Filters' : 'Filters'}
            </button>
            
            <div className="relative inline-block">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="form-select pr-10 pl-3 py-2 bg-gray-100 text-gray-700 rounded-md appearance-none"
              >
                <option value="relevance">Relevance</option>
                <option value="name">Name</option>
                <option value="rating">Rating</option>
                <option value="experience">Experience</option>
                {useLocation && userLocation && <option value="distance">Distance</option>}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                {sortOrder === 'asc' ? <FaSortAmountUp className="text-gray-500" /> : <FaSortAmountDown className="text-gray-500" />}
              </div>
            </div>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              <FaSort />
            </button>
          </div>
        </div>
        
        {/* Expanded Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Specialties Filter */}
              <div>
                <h4 className="font-medium mb-2">Specialties</h4>
                <div className="space-y-2">
                  {specialtyOptions.map(specialty => (
                    <label key={specialty} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.specialties.includes(specialty)}
                        onChange={() => handleFilterChange('specialties', specialty)}
                        className="form-checkbox mr-2"
                      />
                      <span>{specialty}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Rating Filter */}
              <div>
                <h4 className="font-medium mb-2">Minimum Rating</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={filters.rating}
                    onChange={(e) => handleFilterChange('rating', parseInt(e.target.value))}
                    className="form-range w-full"
                  />
                  <span>{filters.rating === 0 ? 'Any' : `${filters.rating}+`}</span>
                </div>
              </div>
              
              {/* Experience Filter */}
              <div>
                <h4 className="font-medium mb-2">Minimum Experience (years)</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="15"
                    step="1"
                    value={filters.experience}
                    onChange={(e) => handleFilterChange('experience', parseInt(e.target.value))}
                    className="form-range w-full"
                  />
                  <span>{filters.experience === 0 ? 'Any' : `${filters.experience}+`}</span>
                </div>
              </div>
              
              {/* Availability Filter */}
              <div>
                <h4 className="font-medium mb-2">Availability</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.availability.includes('weekdays')}
                      onChange={() => handleFilterChange('availability', 'weekdays')}
                      className="form-checkbox mr-2"
                    />
                    <span>Weekdays</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.availability.includes('weekends')}
                      onChange={() => handleFilterChange('availability', 'weekends')}
                      className="form-checkbox mr-2"
                    />
                    <span>Weekends</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.availability.includes('evenings')}
                      onChange={() => handleFilterChange('availability', 'evenings')}
                      className="form-checkbox mr-2"
                    />
                    <span>Evenings</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                <FaTimes className="mr-1" /> Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Mentors List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft">
          {filteredMentors.length === 0 ? (
            <div className="p-8 text-center">
              <FaUserTie className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No Mentors Found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your search criteria or filters</p>
              <button
                onClick={resetFilters}
                className="btn-primary"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredMentors.map((mentor) => (
                <div 
                  key={mentor._id} 
                  className="border border-gray-200 rounded-lg hover:border-primary transition-colors overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        {mentor.profileImage ? (
                          <img
                            src={mentor.profileImage}
                            alt={`${mentor.firstName} ${mentor.lastName}`}
                            className="h-16 w-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-full bg-primary text-white flex items-center justify-center text-xl font-semibold">
                            {mentor.firstName.charAt(0)}
                            {mentor.lastName.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-lg mb-1">
                          {mentor.firstName} {mentor.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">{mentor.expertise || 'Mentor'}</p>
                        
                        <div className="flex items-center mt-1">
                          {renderRatingStars(mentor.rating || 0)}
                          <span className="ml-1 text-sm text-gray-500">({mentor.reviewCount || 0})</span>
                        </div>
                      </div>
                    </div>
                    
                    {useLocation && userLocation && mentor.distance && (
                      <div className="mt-3 text-sm text-gray-600 flex items-center">
                        <FaMapMarkerAlt className="mr-1 text-primary" />
                        <span>
                          {mentor.location?.town ? `${mentor.location.town}, ` : ''}
                          {mentor.location?.county || 'Location unknown'} 
                          {formatDistance(mentor.distance) ? ` · ${formatDistance(mentor.distance)}` : ''}
                        </span>
                      </div>
                    )}
                    
                    <div className="mt-3">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {mentor.bio || 'No bio available.'}
                      </p>
                    </div>
                    
                    <div className="mt-3 flex flex-wrap gap-1">
                      {mentor.specialties && mentor.specialties.slice(0, 3).map((specialty, idx) => (
                        <span 
                          key={idx} 
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                      {mentor.specialties && mentor.specialties.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                          +{mentor.specialties.length - 3} more
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center text-gray-600 text-sm">
                        <FaGraduationCap className="mr-1" />
                        <span>{mentor.experience || 0} years experience</span>
                      </div>
                      
                      <button
                        onClick={() => viewMentorProfile(mentor)}
                        className="text-primary hover:text-primary-dark font-medium"
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Mentor Profile Modal */}
      {selectedMentor && (
        <Modal
          isOpen={showProfileModal}
          onClose={closeProfileModal}
          title={`${selectedMentor.firstName} ${selectedMentor.lastName}`}
          size="xl"
        >
          <MentorProfile mentor={selectedMentor} />
        </Modal>
      )}
    </div>
  );
};

export default MentorResults; 